import { formatActor } from '@/features/audit';
import type { AuditActivityItem } from '@/services';
import { formatDateTimeShortToBrazil } from '@/shared/utils/format-date.util';
import { useMemo, useState } from 'react';
import type { SettingsChange } from '../utils/settings-diff.util';
import { usePaymentSettingsHistory } from './use-payment-settings-history.hook';

export type HistoryFilter = 'all' | 'gateway' | 'catalog';

export type HistoryEntry = {
  id: string;
  actor: string;
  actorEmail?: string;
  isSystem: boolean;
  when: string;
  actionLabel: string;
  kind: Exclude<HistoryFilter, 'all'>;
  description?: string;
  changes: SettingsChange[];
  /** Mudanças além das que o registro guarda (o BFF corta itens muito grandes). */
  hiddenChanges: number;
};

export const HISTORY_FILTER_OPTIONS: { value: HistoryFilter; label: string }[] =
  [
    { value: 'all', label: 'Todas as alterações' },
    { value: 'gateway', label: 'Cobrança pelo gateway' },
    { value: 'catalog', label: 'Catálogo de formas' },
  ];

function toEntry(item: AuditActivityItem): HistoryEntry {
  return {
    id: item.activityId,
    actor: formatActor(item),
    actorEmail: item.actorName ? item.actorEmail : undefined,
    isSystem: item.actorSource === 'SYSTEM',
    when: formatDateTimeShortToBrazil(item.occurredAt),
    actionLabel: item.actionLabel,
    kind:
      item.action === 'product.payment-gateway.update' ? 'gateway' : 'catalog',
    description: item.description,
    changes: item.changes,
    hiddenChanges: Math.max(0, item.changesTotal - item.changes.length),
  };
}

/** Aba "Histórico de alterações": quem mudou o quê, com o "de/para". */
export function usePaymentSettingsHistoryView(productCode: string) {
  const history = usePaymentSettingsHistory(productCode);
  const [filter, setFilter] = useState<HistoryFilter>('all');

  const entries = useMemo(
    () =>
      history.items
        .map(toEntry)
        .filter(entry => filter === 'all' || entry.kind === filter),
    [history.items, filter]
  );

  return {
    entries,
    filter,
    setFilter,
    isLoading: history.isLoading,
    error:
      history.error instanceof Error
        ? history.error.message
        : history.error
          ? 'Não foi possível carregar o histórico.'
          : null,
    hasNextPage: history.hasNextPage,
    isFetchingNextPage: history.isFetchingNextPage,
    loadMore: () => void history.fetchNextPage(),
  };
}
